'use client';

import type { ElementProps } from '@coolblue-development/freddy';
import {
  Box,
  Flex,
  freddyTheme,
  Markdown,
  SpriteIcon,
  Typography
} from '@coolblue-development/freddy';
import showProductPlusTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/showProductPlusTrigger';
import { TrackImmediate } from '@coolblue-development/next-analytics/client';
import { CAMPAIGN_TYPE } from '@features/product/components/OrderBlock/FreeWashingOrDryingProposition/constants';
import type { ProductPlusPromotion } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { useCallback, useEffect, useRef, useState } from 'react';

import ExpandableBox from '../ExpandableBox';
import SubscriptionForm from '../SubscriptionForm';
import { convertHighlightTagToMarkdown } from '../utils/convertHighlightTagToMarkdown';

export default function SubscriptionBox({
  isFreeDrying,
  hasSubscription = false,
  customerEmailAddress,
  promotion
}: {
  isFreeDrying: boolean;
  hasSubscription?: boolean;
  customerEmailAddress?: string;
  promotion: ProductPlusPromotion;
}) {
  const { t } = useTranslation('webshop.productPage.freeWashing');

  const [showEmailForm, setShowEmailForm] = useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(hasSubscription);
  const successMessageRef = useRef<HTMLDivElement>(null);

  const handleSubscriptionSuccess = useCallback(() => {
    setIsSubscribed(true);
    setShowEmailForm(false);
  }, []);

  useEffect(() => {
    if (!hasSubscription && isSubscribed && successMessageRef.current) {
      setTimeout(() => {
        successMessageRef.current?.focus();
      }, 100);
    }
  }, [hasSubscription, isSubscribed]);

  const handleToggleEmailForm = useCallback(() => {
    if (isSubscribed) return;

    setShowEmailForm(prev => !prev);
  }, [isSubscribed]);

  return (
    <Flex direction='column' gap={200}>
      <TrackImmediate trigger={showProductPlusTrigger({ sub_feature: 'free_washing' })} />

      <Markdown
        components={{
          p: ({ children }: ElementProps<'p'>) => (
            <Typography fontWeight={600} mb={0}>
              {children}
            </Typography>
          )
        }}
      >
        {convertHighlightTagToMarkdown(promotion.title)}
      </Markdown>

      <ExpandableBox
        isExpanded={showEmailForm}
        isSubscribed={isSubscribed}
        onClick={handleToggleEmailForm}
        expandedContent={
          <SubscriptionForm
            onSuccessAction={handleSubscriptionSuccess}
            type={isFreeDrying ? CAMPAIGN_TYPE.FreeDrying : CAMPAIGN_TYPE.FreeWashing}
            customerEmailAddress={customerEmailAddress}
            promotion={promotion}
          />
        }
      >
        <Box flex={1}>
          <Typography
            fontWeight={700}
            mb={100}
            {...(!isSubscribed && { color: 'interactiveBase' })}
          >
            {promotion.shortTitle}
          </Typography>

          <Typography variant='body' color='subtle' mb={0}>
            {promotion.description}
          </Typography>

          {isSubscribed && (
            <Flex
              ref={successMessageRef}
              alignItems='center'
              gap={100}
              mt={100}
              role='status'
              aria-atomic='true'
              tabIndex={-1}
              sx={{ outline: 'none' }}
            >
              <SpriteIcon
                name='checkmark'
                color={freddyTheme.color.icon.sentimentPositiveContrast}
              />
              <Typography color='sentimentPositiveContrast'>
                {t('subscriptionSuccessMessage')}
              </Typography>
            </Flex>
          )}
        </Box>
      </ExpandableBox>
    </Flex>
  );
}
